import axios from 'axios'
import { axiosError } from './clientUtils'
import { getLytAccountingApiBase } from './baseUrls'

/**
 * Backend-relative base URL, mirroring lytAccountingClient. Public,
 * pre-authentication endpoints live here.
 */
const BASE_URL = getLytAccountingApiBase()

// --- Types ---

export interface StoreLoginUser {
  id: number
  email: string
  group: string
}

export interface StoreLoginStore {
  storeId: string
  storeName: string
  storeDb: string
  group: string
}

export interface StoreLoginResponse {
  user: StoreLoginUser
  stores: StoreLoginStore[]
}

export interface StoreProviderOption {
  provider: string
  displayName: string
  loginType: string
  integrationId: number | null
}

export type StoreAuthStatus =
  | 'READY'
  | 'PROVIDER_SELECTION_REQUIRED'
  | 'AUTH_REQUIRED'
  | 'NO_ACCOUNT'

export interface StoreSelectionResponse {
  status: StoreAuthStatus
  provider?: StoreProviderOption | null
  apiKey?: string | null
  integrationId?: number | null
  loginType?: string | null
  authRedirectUrl?: string | null
  providers?: StoreProviderOption[] | null
}

export interface StoreInvoiceAccessResponse {
  approved: boolean
  reason?: string | null
  provider?: StoreProviderOption | null
  loginType?: string | null
  authRedirectUrl?: string | null
  invoiceNumber?: string | null
}

// --- Endpoints ---

/**
 * POST /api/v1/auth/login
 * Authenticates a user from burntofi_lytmoblie.user. Returns the user
 * and the stores they are allowed to operate against.
 */
export async function loginUser(payload: {
  email: string
  password: string
}): Promise<StoreLoginResponse> {
  try {
    const { data } = await axios.post<StoreLoginResponse>(`${BASE_URL}/api/v1/auth/login`, payload, {
      headers: { 'Content-Type': 'application/json' },
    })
    return data
  } catch (err) {
    throw axiosError(err)
  }
}

/**
 * POST /api/v1/auth/stores/select
 * Looks up the Lyt Accounting account/integration for a store and
 * returns one of READY / PROVIDER_SELECTION_REQUIRED / AUTH_REQUIRED /
 * NO_ACCOUNT. The backend never regenerates API keys for reauth.
 */
export async function selectLoginStore(payload: {
  userId: number
  storeId: string
  storeDb: string
}): Promise<StoreSelectionResponse> {
  try {
    const { data } = await axios.post<StoreSelectionResponse>(`${BASE_URL}/api/v1/auth/stores/select`, payload, {
      headers: { 'Content-Type': 'application/json' },
    })
    return data
  } catch (err) {
    throw axiosError(err)
  }
}

/**
 * POST /api/v1/auth/stores/select-provider
 * Mirrors selectLoginStore but forces a specific provider. Returns
 * READY or AUTH_REQUIRED.
 */
export async function selectLoginProvider(payload: {
  userId: number
  storeId: string
  storeDb: string
  provider: string
}): Promise<StoreSelectionResponse> {
  try {
    const { data } = await axios.post<StoreSelectionResponse>(
      `${BASE_URL}/api/v1/auth/stores/select-provider`,
      payload,
      { headers: { 'Content-Type': 'application/json' } },
    )
    return data
  } catch (err) {
    throw axiosError(err)
  }
}

/**
 * POST /api/v1/auth/stores/invoice-access
 * "I have an invoice" flow. Backend validates the user, the store,
 * and that the invoice has the Lyt Accounting add-on. When approved
 * the response carries the provider and an OAuth redirect URL.
 */
export async function resolveInvoiceAccess(payload: {
  userId: number
  storeId: string
  storeDb: string
  invoiceNumber: string
}): Promise<StoreInvoiceAccessResponse> {
  try {
    const { data } = await axios.post<StoreInvoiceAccessResponse>(
      `${BASE_URL}/api/v1/auth/stores/invoice-access`,
      payload,
      { headers: { 'Content-Type': 'application/json' } },
    )
    return data
  } catch (err) {
    throw axiosError(err)
  }
}
